import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * SD Video Carousel
 *
 * Owns which card is Active (tall, 9:16) versus Inactive (short, 3:4), keeps the pagination
 * dots in step with it, and drives the card's mute toggle.
 *
 * Everything else is delegated:
 * - scrolling, peek and arrow states belong to the nested `sd-carousel-component`
 * - thumbnail, deferred loading and playback belong to Horizon's `deferred-media`
 *
 * Refs are scoped to the nearest `-component` ancestor, so the cards and dots inside
 * `sd-carousel-component` belong to that component and are not visible on `this.refs`.
 * They are resolved by selector instead.
 *
 * @extends {Component}
 */

const CARD = '.sd-video-card';
const ACTIVE = 'sd-video-card--active';
const DOT = '.sd-carousel-dots__dot';
const PREV = '.sd-carousel-arrows__button--prev';
const NEXT = '.sd-carousel-arrows__button--next';
/** The card's own mute toggle, which replaces the browser's. */
const SOUND = '[data-sd-sound]';
/** The card's own play/pause control, covering the whole card. */
const PLAY = '[data-sd-play]';
/** Wrapper around an uploaded video; carries the card's playback state as `data-state`. */
const PLAYER = '[data-sd-player]';
/** Only devices that genuinely hover; a touch screen reports a spurious hover on tap. */
const CAN_HOVER = '(hover: hover) and (pointer: fine)';
/** Hover intent, so dragging the cursor across the row does not start every video in it. */
const HOVER_INTENT_MS = 140;
/** Gap the design specifies between an arrow and the nearest card. */
const ARROW_GAP = 20;
/** Arrows never reach the screen edge. */
const ARROW_EDGE_MIN = 5;

class SDVideoCarousel extends Component {
  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {ResizeObserver|null} */
  #resizeObserver = null;

  /** @type {MediaQueryList|null} */
  #hoverQuery = null;

  /** @type {ReturnType<typeof setTimeout>|undefined} */
  #hoverTimer;

  /** @type {Element|null} */
  #hoverCard = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Fires once per card, when deferred-media swaps the poster for the real video.
    this.addEventListener(ThemeEvents.mediaStartedPlaying, this.#onMediaStarted, { signal });

    // `play`/`pause` do not bubble, but a capturing listener still sees them. These keep the
    // overlays honest when playback changes without any click of ours (ended, native controls).
    this.addEventListener('play', this.#onPlaybackChange, { signal, capture: true });
    this.addEventListener('pause', this.#onPlaybackChange, { signal, capture: true });
    this.addEventListener('ended', this.#onPlaybackChange, { signal, capture: true });

    // Playback state for the uploaded-video player. `playing` is the only event that proves a
    // frame is on screen, so it is what retires the poster and the spinner.
    this.addEventListener('playing', this.#onPlayerState, { signal, capture: true });
    this.addEventListener('waiting', this.#onPlayerState, { signal, capture: true });
    this.addEventListener('emptied', this.#onPlayerState, { signal, capture: true });

    this.addEventListener('click', this.#onClick, { signal });

    // Mobile shows one card, so whichever card the rail settles on becomes the Active (tall)
    // one. Without this, scrolling to an Inactive card leaves it short inside a row still
    // reserved for a tall card, which reads as a large gap above and below it.
    this.#track?.addEventListener('scrollend', this.#onScrollEnd, { signal });

    if (this.hasAttribute('data-play-on-hover')) {
      this.#hoverQuery = window.matchMedia(CAN_HOVER);
      this.addEventListener('pointerover', this.#onPointerOver, { signal });
      this.addEventListener('pointerleave', this.#cancelHover, { signal });
    }

    this.#setActive(this.#cards[0], false);

    // Hands the Active state over from the CSS first-card fallback to this component.
    this.classList.add('sd-video-carousel--ready');

    // Reserve the row's height before anything can animate, then keep it correct as the
    // cards are re-sized by the viewport. Called synchronously on purpose: layout is already
    // queryable here, and a rAF callback never fires in a background tab, which would leave
    // the row unpinned and the jump back in place.
    this.#syncArrowSpacing();
    this.#reserveRowHeight();

    const track = this.#track;
    if (track) {
      let width = track.clientWidth;
      this.#resizeObserver = new ResizeObserver(() => {
        if (track.clientWidth === width) return;
        width = track.clientWidth;
        this.#syncArrowSpacing();
        this.#reserveRowHeight();
      });
      this.#resizeObserver.observe(track);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#cancelHover();
  }

  /** @returns {Element[]} */
  get #cards() {
    return Array.from(this.querySelectorAll(CARD));
  }

  /** @returns {Element[]} */
  get #dots() {
    return Array.from(this.querySelectorAll(DOT));
  }

  /**
   * Promotes a card, starts its video, and scrolls it into view.
   *
   * Public because the pagination dots reach it through the component framework's
   * `on:click="#id/selectCard/N"` binding rather than through a bubbled DOM event.
   *
   * @param {number} index - Zero-based card index
   */
  selectCard(index) {
    const card = this.#cards[Number(index)];
    if (!card) return;

    this.#pauseOthers(card);
    this.#play(card);
    this.#setActive(card);
  }

  /**
   * Steps the Active card back one.
   *
   * Public because the prev arrow reaches it through the framework's
   * `on:click="#id/previous"` binding.
   */
  previous() {
    this.selectCard(this.#activeIndex - 1);
  }

  /**
   * Steps the Active card forward one.
   *
   * Public because the next arrow reaches it through the framework's
   * `on:click="#id/next"` binding.
   */
  next() {
    this.selectCard(this.#activeIndex + 1);
  }

  /** @returns {number} */
  get #activeIndex() {
    return this.#cards.findIndex((card) => card.classList.contains(ACTIVE));
  }

  /**
   * Disables the arrows at the ends of the card list.
   *
   * Deliberately driven by the Active index rather than scroll position: the arrows move the
   * Active card, so a row that does not overflow still has a meaningful first and last.
   *
   * @param {number} index
   */
  #syncArrows(index) {
    const prev = this.querySelector(PREV);
    const next = this.querySelector(NEXT);
    const last = this.#cards.length - 1;

    if (prev instanceof HTMLButtonElement) prev.disabled = index <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = index >= last;
  }

  /** @returns {HTMLElement|null} */
  get #track() {
    return this.querySelector('.sd-video-carousel__track');
  }

  /**
   * Pins the row to the height of an Active card.
   *
   * Both cards animate at once when the Active state moves, so mid-transition neither is at
   * full 9:16 and the row would otherwise shrink and grow again, moving every section below
   * this one. Measured rather than computed because the CTA's height comes from theme tokens.
   */
  /**
   * Places the desktop arrows a fixed 20px from the cards.
   *
   * Measured rather than expressed in CSS because the real gutter is not available there:
   * `--page-margin` stops growing at 40px, while the space beside the content column is
   * usually wider. Where the gutter can hold the arrow plus its gap, the arrow moves out into
   * it and the row keeps full width. Where it cannot, the row gives back the shortfall as
   * padding, so the gap holds and the button still lands inside the section.
   *
   * Measured from the viewport, not the track: the track's position shifts when the padding
   * below is applied, which would feed back into the next measurement.
   */
  #syncArrowSpacing() {
    const viewport = this.querySelector('.sd-video-carousel__viewport');
    const section = this.closest('.section');
    const arrow = this.querySelector(PREV);
    if (!viewport || !section || !arrow) return;

    const arrowWidth = arrow.getBoundingClientRect().width;
    if (!arrowWidth) return;

    const needed = arrowWidth + ARROW_GAP;
    const gutter = viewport.getBoundingClientRect().left - section.getBoundingClientRect().left;

    const available = Math.max(0, gutter - ARROW_EDGE_MIN);
    const outset = Math.min(needed, available);

    viewport.style.setProperty('--sd-video-arrow-outset', `${Math.round(outset)}px`);
    viewport.style.setProperty('--sd-video-arrow-reserve', `${Math.round(needed - outset)}px`);
  }

  #reserveRowHeight() {
    const track = this.#track;
    const card = this.#cards[0];
    if (!track || !card) return;

    const media = card.querySelector('.sd-video-card__media');
    if (!media) return;

    // Computed, not measured: card width is set by flex-basis and never animates, whereas the
    // media's height does. Measuring it would bake in whatever frame the transition was on.
    const width = media.getBoundingClientRect().width;
    if (!width) return;

    const cta = card.querySelector('.sd-video-card__cta');
    const ctaHeight = cta ? cta.getBoundingClientRect().height : 0;
    const gap = ctaHeight ? parseFloat(getComputedStyle(card).rowGap) || 0 : 0;

    const height = width * (16 / 9) + gap + ctaHeight;
    track.style.setProperty('--sd-video-row-height', `${Math.ceil(height)}px`);
  }

  /**
   * Whether a card's media is actually playing, rather than what deferred-media believes.
   * A cross-origin iframe exposes no state, so its own flag is the only signal available.
   *
   * @param {Element|null} media
   * @returns {boolean}
   */
  #isPlaying(media) {
    if (!media) return false;
    const video = media.querySelector('video');
    if (video instanceof HTMLVideoElement) return !video.paused && !video.ended;
    return 'isPlaying' in media ? Boolean(media.isPlaying) : false;
  }

  /**
   * Starts a card's video, covering the case deferred-media does not.
   * Once `data-media-loaded` is set, its poster handler short-circuits and never calls play,
   * so a card that has been played before would otherwise stay frozen on click.
   *
   * @param {Element} card
   */
  #play(card) {
    // Uploaded video: the element is already in the page with `preload="none"`, so `play()` runs
    // inside the tap that asked for it. WebKit refuses to start a video that was not in the DOM
    // and visible when the gesture happened, which is what made the old deferred markup need two
    // or three taps. Nothing may be awaited before this call or the gesture is spent.
    const video = card.querySelector(`${PLAYER} video`);
    if (video instanceof HTMLVideoElement) {
      const player = video.closest(PLAYER);
      if (player && video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        this.#setPlayerState(player, 'loading');
      }
      video.play().catch(() => {
        if (player) this.#setPlayerState(player, 'idle');
      });
      return;
    }

    // External video still runs through deferred-media, which suits an iframe.
    const media = card.querySelector('deferred-media');
    if (!media) return;

    if (media.hasAttribute('data-media-loaded')) {
      if ('playMedia' in media && typeof media.playMedia === 'function') media.playMedia();
    } else if ('showDeferredMedia' in media && typeof media.showDeferredMedia === 'function') {
      media.showDeferredMedia();
    }
  }

  /**
   * Pauses every player except the one in the given card.
   *
   * Re-dispatching the theme event instead would pause the player that just started, because
   * deferred-media listens for that event on itself too.
   *
   * @param {Element} activeCard
   */
  #pauseOthers(activeCard) {
    for (const video of this.querySelectorAll(`${PLAYER} video`)) {
      if (video.closest(CARD) === activeCard) continue;
      if (video instanceof HTMLVideoElement) video.pause();
    }

    for (const media of this.querySelectorAll('deferred-media')) {
      if (media.closest(CARD) === activeCard) continue;
      if ('pauseMedia' in media && typeof media.pauseMedia === 'function') media.pauseMedia();
    }
  }

  /**
   * Marks the dot matching the Active card, so pagination tracks the Active card rather than
   * scroll position. The nested carousel is handed a different ref name so it leaves them alone.
   *
   * @param {number} index
   */
  #syncDots(index) {
    this.#dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  /**
   * Promotes a card to Active and demotes the rest.
   *
   * A card that is already Active is left alone. One tap otherwise lands here three times
   * for the same card — from the media-started event, from the click handler, and again
   * from the video's own `play` — and each pass re-issues the smooth scroll, the last of
   * them part-way through the promote transition. That restart is what made the expand
   * stutter on mobile.
   *
   * @param {Element|null|undefined} card
   * @param {boolean} [scrollIntoView]
   */
  #setActive(card, scrollIntoView = true) {
    if (!card || card.classList.contains(ACTIVE)) return;

    const cards = this.#cards;
    const index = cards.indexOf(card);

    for (const item of cards) item.classList.toggle(ACTIVE, item === card);

    this.#syncDots(index);
    this.#syncArrows(index);

    if (scrollIntoView) this.#scrollTo(index);
  }

  /**
   * Asks the nested carousel to bring a card into view.
   *
   * @param {number} index
   */
  #scrollTo(index) {
    if (index < 0) return;
    // Mobile shows one card, so a tapped peeking card centres itself rather than aligning to
    // the start, which would leave it hard against the gutter.
    if (window.innerWidth < 600) {
      this.#cards[index]?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      return;
    }

    const carousel = this.querySelector('sd-carousel-component');
    if (carousel && 'select' in carousel && typeof carousel.select === 'function') {
      carousel.select(index);
    }
  }

  /** @param {Event} event */
  #onMediaStarted = (event) => {
    const resource = /** @type {CustomEvent} */ (event).detail?.resource;
    if (!(resource instanceof Element)) return;
    this.#setActive(resource.closest(CARD));
  };

  /**
   * Keeps a player's `data-state` honest for the events that are not a plain play or pause.
   *
   * @param {Event} event
   */
  #onPlayerState = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLVideoElement)) return;

    const player = target.closest(PLAYER);
    if (!player) return;

    if (event.type === 'playing') this.#setPlayerState(player, 'playing');
    else if (event.type === 'waiting') this.#setPlayerState(player, 'loading');
    else this.#setPlayerState(player, 'idle');
  };

  /**
   * @param {Element} player
   * @param {'idle'|'loading'|'playing'|'paused'} state
   */
  #setPlayerState(player, state) {
    player.setAttribute('data-state', state);

    const button = player.querySelector(PLAY);
    if (!(button instanceof HTMLElement)) return;

    const label = state === 'playing' ? button.dataset.pauseLabel : button.dataset.playLabel;
    if (label) button.setAttribute('aria-label', label);
  }

  /** @param {Event} event */
  #onPlaybackChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest(CARD);
    if (!card) return;

    const player = target.closest(PLAYER);

    if (event.type === 'play') {
      if (player) this.#setPlayerState(player, 'loading');
      this.#pauseOthers(card);
      this.#setActive(card);
      return;
    }

    if (player) {
      this.#setPlayerState(player, 'paused');
      return;
    }

    // A video that reaches its end leaves deferred-media still believing it is playing, so the
    // toggle keeps showing a pause icon over stopped footage and the card offers no way back.
    // Routing it through `pauseMedia` flips the hint; pausing an already-paused video is a
    // no-op and fires no further event.
    const media = card.querySelector('deferred-media');
    if (media && 'pauseMedia' in media && typeof media.pauseMedia === 'function') media.pauseMedia();
  };

  /**
   * Promotes and plays the hovered card, when the merchant has enabled it.
   *
   * `pointerover` rather than `pointerenter` so one delegated listener covers cards the theme
   * editor adds later. Moving within the same card is ignored, and the intent delay is
   * cancelled on leaving, so sweeping the cursor across the row does not start every video.
   *
   * @param {Event} event
   */
  #onPointerOver = (event) => {
    if (!this.#hoverQuery?.matches) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest(CARD);
    if (!card || card === this.#hoverCard) return;

    this.#hoverCard = card;
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = setTimeout(() => this.selectCard(this.#cards.indexOf(card)), HOVER_INTENT_MS);
  };

  #cancelHover = () => {
    clearTimeout(this.#hoverTimer);
    this.#hoverCard = null;
  };

  #onScrollEnd = () => {
    // ponytail: width check, not a stored MediaQueryList. Fires once per settle.
    if (window.innerWidth >= 600) return;

    const track = this.#track;
    if (!track) return;

    const middle = track.scrollLeft + track.clientWidth / 2;
    const cards = this.#cards;

    let nearest = 0;
    let best = Infinity;
    cards.forEach((card, i) => {
      const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - middle);
      if (distance < best) {
        best = distance;
        nearest = i;
      }
    });

    // Promote only. Scrolling past a card is not a request to play it.
    if (nearest !== this.#activeIndex) this.#setActive(cards[nearest], false);
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Dots are handled by `selectCard`, dispatched by the framework, not by this listener.
    if (target.closest(DOT)) return;

    const sound = target.closest(SOUND);
    if (sound instanceof HTMLElement) {
      this.#toggleSound(sound);
      return;
    }

    const play = target.closest(PLAY);
    if (play) {
      const card = play.closest(CARD);
      if (!card) return;

      const video = card.querySelector(`${PLAYER} video`);
      if (video instanceof HTMLVideoElement && !video.paused) {
        video.pause();
        return;
      }

      // `#play` first, so `play()` is the first thing this gesture does. Promoting the card
      // scrolls the rail, and doing that ahead of the call has been enough to lose the gesture.
      this.#pauseOthers(card);
      this.#play(card);
      this.#setActive(card);
      return;
    }

    const control = target.closest('.deferred-media__poster-button');
    if (!control) return;

    const card = control.closest(CARD);
    if (!card) return;

    // The toggle button pauses as well as plays, so let its own handler settle first.
    if (control.matches('.video-interaction-hint')) {
      setTimeout(() => {
        if (!this.#isPlaying(card.querySelector('deferred-media'))) return;
        this.#pauseOthers(card);
        this.#setActive(card);
      }, 0);
      return;
    }

    this.#pauseOthers(card);
    this.#play(card);
    this.#setActive(card);
  };

  /**
   * Flips a card's audio. The video is served muted so it can start without a gesture, so
   * this is the only way to hear it now that the browser's own controls are gone.
   *
   * @param {HTMLElement} button
   */
  #toggleSound(button) {
    const video = button.closest(CARD)?.querySelector('video');
    if (!video) return;

    video.muted = !video.muted;

    button.setAttribute('aria-pressed', String(!video.muted));

    // Both labels are handed over as data attributes so the copy stays translatable in Liquid.
    const label = video.muted ? button.dataset.unmuteLabel : button.dataset.muteLabel;
    if (label) button.setAttribute('aria-label', label);

    const [off, on] = button.querySelectorAll('.sd-video-card__sound-icon');
    off?.classList.toggle('hidden', !video.muted);
    on?.classList.toggle('hidden', video.muted);
  }
}

if (!customElements.get('sd-video-carousel-component')) {
  customElements.define('sd-video-carousel-component', SDVideoCarousel);
}
